import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Image, Input, message, Popconfirm, Select, Space, Table, Tag } from 'antd';
import { getMealPlannerRecipes, setMealPlannerStatus } from '@/Axios/api/mealPlanner';
import { MealPlannerFilters, MealPlannerRecipeRow } from '@/Interfaces/I_mealPlanner';
import Details from './Details';
import useMealPlannerList from './useMealPlannerList';

export default function Recipes() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<MealPlannerFilters>({ page: 0, size: 24 });
  const categoryId = params.get('categoryId') || undefined;
  const [revision, setRevision] = useState(0);
  const [detailId, setDetailId] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const requestFilters = React.useMemo(() => ({ ...filters, categoryId }), [filters, categoryId]);
  const { data, loading, error } = useMealPlannerList(
    getMealPlannerRecipes,
    requestFilters,
    revision,
  );
  const toggle = async (row: MealPlannerRecipeRow) => {
    const id = row.recipe.recipeId;
    setBusyId(id);
    try {
      await setMealPlannerStatus('recipes', id, row.disabled === false);
      message.success('Recipe status updated');
      setDetailId(undefined);
    } catch {
      message.error('Update could not be confirmed. Refresh to check the current status.');
    } finally {
      setBusyId(undefined);
      setRevision((value) => value + 1);
    }
  };
  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Recipe ID or name"
          maxLength={100}
          allowClear
          data-testid="meal-planner-recipe-search"
          onSearch={(search) =>
            setFilters((value) => ({ ...value, page: 0, search: search || undefined }))
          }
        />
        <Input.Search
          key={categoryId || 'all'}
          placeholder="Category ID (optional)"
          defaultValue={categoryId}
          maxLength={64}
          allowClear
          data-testid="meal-planner-recipe-category-filter"
          onSearch={(id) => {
            setFilters((value) => ({ ...value, page: 0 }));
            setParams(id ? { categoryId: id.trim() } : {});
          }}
        />
        <Select
          placeholder="All statuses"
          allowClear
          style={{ width: 160 }}
          data-testid="meal-planner-recipe-status-filter"
          options={[
            { value: 'enabled', label: 'Enabled' },
            { value: 'disabled', label: 'Disabled' },
          ]}
          onChange={(status?: string) =>
            setFilters((value) => ({
              ...value,
              page: 0,
              disabled: status ? status === 'disabled' : undefined,
            }))
          }
        />
        <Button
          onClick={() => setRevision((value) => value + 1)}
          data-testid="meal-planner-recipe-refresh"
        >
          Refresh
        </Button>
      </Space>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Table<MealPlannerRecipeRow>
        rowKey={(row) => row.recipe.recipeId}
        dataSource={data?.items || []}
        loading={loading}
        pagination={{
          current: filters.page + 1,
          pageSize: filters.size,
          total: data?.pagination.totalElements || 0,
          showSizeChanger: true,
          pageSizeOptions: [24, 48, 100],
          onChange: (page, size) =>
            setFilters((value) => ({ ...value, page: size !== value.size ? 0 : page - 1, size })),
        }}
        columns={[
          { title: 'ID', render: (_, row) => row.recipe.recipeId },
          {
            title: 'Image',
            render: (_, row) =>
              row.recipe.heroImageUrl ? <Image width={60} src={row.recipe.heroImageUrl} /> : '—',
          },
          {
            title: 'Name',
            render: (_, row) => (
              <Button
                type="link"
                onClick={() => setDetailId(row.recipe.recipeId)}
                data-testid={`meal-planner-recipe-detail-${row.recipe.recipeId}`}
              >
                {row.recipe.name || row.recipe.recipeId}
              </Button>
            ),
          },
          { title: 'Time', render: (_, row) => row.recipe.totalTimeDisplayText || '—' },
          { title: 'Difficulty', dataIndex: 'difficulty', render: (value) => value ?? '—' },
          { title: 'Ingredients', render: (_, row) => row.recipe.ingredientCount ?? '—' },
          {
            title: 'Status',
            render: (_, row) => (
              <Tag color={row.disabled === false ? 'green' : undefined}>
                {row.disabled == null ? 'Unknown' : row.disabled ? 'Disabled' : 'Enabled'}
              </Tag>
            ),
          },
          {
            title: 'Action',
            render: (_, row) => (
              <Popconfirm
                title={`${row.disabled === false ? 'Disable' : 'Enable'} this recipe?`}
                onConfirm={() => toggle(row)}
                disabled={!!busyId}
              >
                <Button
                  loading={busyId === row.recipe.recipeId}
                  disabled={!!busyId || loading}
                  data-testid={`meal-planner-recipe-toggle-${row.recipe.recipeId}`}
                >
                  {row.disabled === false ? 'Disable' : 'Enable'}
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
      <Details kind="recipes" id={detailId} onClose={() => setDetailId(undefined)} />
    </>
  );
}
